import * as v from "valibot";

export const attachContext = <T>(context: any): v.TransformAction<T, T> & { customContext: any } => {
  const idTransform = v.transform<T, T>((x) => x) as ReturnType<typeof attachContext<T>>;
  idTransform.customContext = context;
  return idTransform;
};

export type DictionaryItems<T> = readonly { readonly label: string; readonly value: T }[];
type DictionaryValueType<It extends DictionaryItems<any>> = It extends DictionaryItems<infer T> ? T : never;
export const Dictionary = Symbol("Dictionary");
export const dictionarySchema = <Items extends DictionaryItems<any>>(items: Items) =>
  v.pipe(
    v.custom<DictionaryValueType<Items>>((val) => items.findIndex((it) => it.value == val) != -1),
    v.brand(Dictionary),
    attachContext(items),
  );
export type InferDictionary<It extends DictionaryItems<any>> = v.InferOutput<ReturnType<typeof dictionarySchema<It>>>;

export const MultiDictionary = Symbol("MultiDictionary");
export const multiDictionarySchema = <Items extends DictionaryItems<any>>(items: Items) =>
  v.pipe(
    v.array(v.custom<DictionaryValueType<Items>>((val) => items.findIndex((it) => it.value == val) != -1)),
    v.brand(MultiDictionary),
    attachContext(items),
  );
export type InferMultiDictionary<It extends DictionaryItems<any>> = v.InferOutput<
  ReturnType<typeof multiDictionarySchema<It>>
>;

export function dictionaryItems<T>(
  schema: ReturnType<typeof dictionarySchema | typeof multiDictionarySchema>,
): DictionaryItems<T> {
  return schema.pipe[2].customContext;
}

export const DateRange = Symbol("DateRange");
export const dateRangeSchema = v.pipe(
  v.object({
    dateFrom: v.pipe(v.string(), v.isoDate()),
    dateTo: v.pipe(v.string(), v.isoDate()),
  }),
  v.brand(DateRange),
);
export type DateRange = v.InferOutput<typeof dateRangeSchema>;

export const Pagination = Symbol("Pagination");
export const paginationSchema = (orders: DictionaryItems<string>) =>
  v.pipe(
    v.object({
      page: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1))),
      pageSize: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1))),
      order: v.optional(dictionarySchema(orders)),
    }),
    v.brand(Pagination),
  );
export type Pagination = v.InferOutput<ReturnType<typeof paginationSchema>>;

export const IntRange = Symbol("IntRange");
export const intRangeSchema = (min: number, max: number) =>
  v.pipe(
    v.object({
      low: v.pipe(v.number(), v.integer(), v.minValue(min)),
      high: v.pipe(v.number(), v.integer(), v.maxValue(max)),
    }),
    v.brand(IntRange),
  );
export type IntRange = v.InferOutput<ReturnType<typeof intRangeSchema>>;

export function bounds(schema: ReturnType<typeof intRangeSchema>): { min: number; max: number } {
  const min = schema.pipe[0].entries.low.pipe[2].requirement;
  const max = schema.pipe[0].entries.high.pipe[2].requirement;
  return {
    min,
    max,
  };
}

export const refSchema = <S extends symbol>(refBrand: S) => v.pipe(v.string(), v.uuid(), v.brand(refBrand));
type InferRef<S extends symbol> = v.InferOutput<ReturnType<typeof refSchema<S>>>;

export const RefList = Symbol("RefList");
export const refListSchema = <S extends symbol>(refBrand: S) => v.pipe(v.array(refSchema(refBrand)), v.brand(RefList));
type InferRefList<S extends symbol> = v.InferOutput<ReturnType<typeof refListSchema<S>>>;

export const CountryRef = Symbol("CountryRef");
export type CountryRef = InferRef<typeof CountryRef>;
export type CountryRefList = InferRefList<typeof CountryRef>;

export const OperatorRef = Symbol("OperatorRef");
export type OperatorRef = InferRef<typeof OperatorRef>;
export type OperatorRefList = InferRefList<typeof OperatorRef>;

export const AmenityRef = Symbol("AmenityRef");
export type AmenityRef = InferRef<typeof AmenityRef>;
export type AmenityRefList = InferRefList<typeof AmenityRef>;

export function getBrand(schema: v.BaseSchema<any, any, any>): v.BrandName | null {
  if (!("pipe" in schema)) {
    return null;
  }
  const pipe = schema.pipe as [v.BaseSchema<any, any, any>, ...v.PipeItem<any, any, any>[]];

  for (const pipeItem of pipe) {
    if (pipeItem.kind == "transformation" && pipeItem.type == "brand") {
      const brandTransform = pipeItem as v.BrandAction<any, v.BrandName>;
      return brandTransform.name;
    }
  }

  return getBrand(pipe[0]);
}

export function isOfBrand(schema: v.BaseSchema<any, any, any>, brand: v.BrandName): boolean {
  return getBrand(schema) === brand;
}

export const Destination = Symbol("Destination");
export const destinationSchema = v.pipe(
  v.array(
    v.object({
      type: v.picklist(["location", "region", "country", "town", "tlr"]),
      ref: v.pipe(v.string(), v.uuid()),
    }),
  ),
  v.brand(Destination),
);
export type Destination = v.InferOutput<typeof destinationSchema>;

export const YachtName = Symbol("YachtName");
export const yachtNameSchema = v.pipe(v.string(), v.brand(YachtName));
export type YachtName = v.InferOutput<typeof yachtNameSchema>;

export const ModelName = Symbol("ModelName");
export const modelNameSchema = v.pipe(v.string(), v.brand(ModelName));
export type ModelName = v.InferOutput<typeof modelNameSchema>;

export const BuilderName = Symbol("BuilderName");
export const builderNameSchema = v.pipe(v.string(), v.brand(BuilderName));
export type BuilderName = v.InferOutput<typeof builderNameSchema>;
